import { useState, useRef, useEffect, useMemo, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import Navbar2 from '../component/navbar2';
import Footer from '../components/BrandFooter';
import Stars from '@/components/Stars';

const BRAND = '#1976d2';
const BRAND_DEEP = '#3171af';
const BRAND_BRIGHT = '#5B9BD5';
const BRAND_SOFT = '#e1f5fe';
const CREAM = '#FCFDFF';
const PAPER = '#F1F0E4';
const INK = '#17263A';
const INK2 = '#42505F';
const MUTED = '#6E7888';
const LINE = '#E5E8EA';
const GREEN = '#15824E';

function FadeIn({ children, delay = 0, className = '' }: { children: ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  const tmr = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(entries => {
      const e = entries[0];
      if (e && e.isIntersecting) { tmr.current = setTimeout(() => setVis(true), delay); obs.disconnect(); }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => { obs.disconnect(); if (tmr.current) clearTimeout(tmr.current); };
  }, [delay]);
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}>
      {children}
    </div>
  );
}

function CounterStat({ to, suffix = '' }: { to: number; suffix?: string }) {
  const [val, setVal] = useState(0);
  const started = useRef(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry && entry.isIntersecting && !started.current) {
        started.current = true;
        if (to === 0) { obs.disconnect(); return; }
        const step = Math.max(1, Math.ceil(to / 40));
        let n = 0;
        const t = setInterval(() => { n += step; if (n >= to) { setVal(to); clearInterval(t); } else setVal(n); }, 22);
        obs.disconnect();
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [to]);
  return <span ref={ref}>{val}{suffix}</span>;
}

const TABS = [
  {
    id: 'score', label: 'Readiness score',
    h: 'A score that means something.',
    p: 'Your readiness across financials, traction, team, market and deal structure — scored on one scale, so you always know how close you are to being investor-ready.',
    features: ['Free static score to see your gaps', 'Living score that moves as you improve', 'An alert the moment you hit investor-ready'],
    price: 'Free to start · Living score from $7.99', priceSub: '/ month',
    rows: [['Financials', '82%'], ['Traction', '71%'], ['Team', '76%'], ['Market & deal', '54%'], ['Overall', '68% — target 80%']],
  },
  {
    id: 'assess', label: 'Capital Assessment',
    h: 'Capital Assessment.',
    p: 'A focused assessment plus a 40-minute session with an advisor who has been on both sides of the table. You leave with a clear, prioritised plan — and the fee comes off any package you take.',
    features: ['Full readiness assessment', '40-minute 1:1 advisor session', 'Fee fully deductible from any package'],
    price: '$99', priceSub: '· one-time, credited back',
    rows: [['Assessment', 'Full readiness'], ['Advisor time', '40 minutes'], ['Output', 'Prioritised plan'], ['Fee', 'Credited to package']],
  },
  {
    id: 'advisory', label: 'Advisory',
    h: 'Advisory that moves the score.',
    p: "We work alongside you to fix what's holding the raise back — the financial model, the pitch narrative, the data room, the KPIs investors actually ask about — until you're investor-ready.",
    features: ['Investor-grade financial model', 'Pitch deck & data room', 'KPI dashboard & readiness plan to 80%'],
    price: 'From $2,999', priceSub: '· three package tiers',
    rows: [['Foundations', '$2,999'], ['Investor-Ready', '$6,999'], ['Raise-Ready', '$11,999'], ['CA fee', 'Deducted']],
  },
  {
    id: 'raise', label: 'Capital raising',
    h: 'Get raise-ready.',
    p: "When you're investor-ready, we help you prepare and approach the right investors, leaning on a deal network across MENA, the UK and the US. Full raise execution is rolling out.",
    features: ['Investor targeting & introductions prep', 'Investor prep across three markets', 'Success-fee engagements rolling out'],
    price: 'Advisory now', priceSub: '· raise support rolling out',
    rows: [['Structure', 'Retainer + monthly'], ['Success fee', '2% of capital raised'], ['Markets', 'MENA · UK · USA'], ['For', 'Investor-ready founders']],
  },
];

const MARKETS = [
  { flag: 'MENA', h: 'Home market', p: "Our base and first-mover advantage — deep roots with the region's founders and an understanding of how they actually raise." },
  { flag: 'London', h: 'Deal network', p: "An active deal and investor network in one of the world's primary capital hubs — where many emerging-market rounds get done." },
  { flag: 'USA', h: 'On the ground', p: "A partner presence in the US to open the largest venture market to founders who'd otherwise never get in the room." },
];

const LADDER = [
  { name: 'Free score', sub: 'See your gaps', price: '$0' },
  { name: 'Living score', sub: 'Track & alerts', price: '$7.99 / mo' },
  { name: 'Capital Assessment', sub: 'Advisor session', price: '$99 · credited' },
];

const TIERS = [
  {
    tag: 'Get the basics right', name: 'Foundations', price: '$2,999', pop: false,
    features: ['Investor-grade financial model', 'Pitch narrative & core deck', 'Readiness plan to lift your score'],
    cta: 'Choose Foundations',
  },
  {
    tag: 'Get to 80%', name: 'Investor-Ready', price: '$6,999', pop: true,
    features: ['Everything in Foundations', 'Full pitch deck & data room', 'KPI dashboard & hands-on score lift to 80%'],
    cta: 'Choose Investor-Ready',
  },
  {
    tag: 'Be raise-ready', name: 'Raise-Ready', price: '$11,999', pop: false,
    features: ['Everything in Investor-Ready', 'Investor targeting & intro prep', 'Mock pitches & dedicated advisor'],
    cta: 'Choose Raise-Ready',
  },
];

const REVIEWS = [
  { av: 'L', q: "Conncct helped us close our seed round in 6 weeks. The readiness work turned a vague raise into a closed round.", name: 'Lara Hassan', role: 'Founder & CEO, TechVenture MENA' },
  { av: 'O', q: "The financial model and data room finally stood up to investor scrutiny. Honest advice, real intros, incredible results.", name: 'Omar Al-Rashid', role: 'Co-Founder, GreenScale UAE' },
  { av: 'P', q: "We secured $2.4M Series A using Conncct. Score climbed to investor-ready and the right investor intros followed.", name: 'Priya Sharma', role: 'CEO, HealthBridge Startup' },
];

const FAQS = [
  { q: 'Who is Conncct for?', a: "Emerging-market founders who want to raise but aren't sure they're ready — or know they're not, and want a clear path to get there. From pre-seed through Series A." },
  { q: 'What is the readiness score?', a: "A single measure of how investor-ready you are across financials, traction, team, market and deal structure. It starts free and static; subscribe and it becomes a living score that moves as you improve, with an alert when you cross the 80% investor-ready line." },
  { q: "Do you guarantee we'll raise?", a: "No — no honest advisor can. What we do is make you genuinely investor-ready and improve your odds: a strong model, a clear story, a complete data room, and introductions to investors who fit your stage." },
  { q: 'How does the success fee work?', a: "Today we focus on getting you investor-ready through advisory. Full raise execution — where we run the process alongside you, including success-fee engagements — is rolling out, so ask us about timing for your raise." },
  { q: 'Is the $99 assessment worth it before a package?', a: "It's the best place to start — a full assessment and a 40-minute advisor session that give you a prioritised plan. The fee is credited back against any package you choose, so you lose nothing by starting there." },
  { q: 'Which markets can you help me raise in?', a: "MENA is our home market, with an active deal network in London and a partner presence in the US — so we can support a raise across all three from day one." },
];

function Eyebrow({ children, dark = false }: { children: ReactNode; dark?: boolean }) {
  return (
    <span style={{
      fontWeight: 700, fontSize: 12.5, letterSpacing: '0.16em', textTransform: 'uppercase' as const,
      color: dark ? BRAND_BRIGHT : '#fff',
      background: dark ? INK : BRAND,
      border: dark ? `1px solid ${BRAND_BRIGHT}33` : undefined,
      display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999,
    }}>
      {children}
    </span>
  );
}

function CheckIcon({ color = GREEN }: { color?: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 3 }}>
      <path d="M5 12l5 5L20 7" stroke={color} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function CapitalRaising() {
  const [activeTab, setActiveTab] = useState('score');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const isLoggedIn = useMemo(() => {
    try {
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      return !!(userStr && token);
    } catch { return false; }
  }, []);
  const ctaPath = isLoggedIn ? '/dashboard' : '/signup';

  const tab = TABS.find(t => t.id === activeTab) ?? TABS[0];

  return (
    <div style={{ background: CREAM, fontFamily: "'Montserrat', system-ui, sans-serif", color: INK, lineHeight: 1.55 }}>

      <Navbar2 />

      {/* ── Hero ── */}
      <header style={{ background: `radial-gradient(120% 90% at 80% -10%, ${BRAND_SOFT} 0%, ${CREAM} 55%)`, position: 'relative', overflow: 'hidden' }}>
        <div aria-hidden="true" style={{ position: 'absolute', borderRadius: '50%', filter: 'blur(10px)', width: 380, height: 380, background: BRAND, top: -120, right: -80, opacity: 0.35, zIndex: 0 }} />
        <div className="max-w-[1160px] mx-auto px-6">
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center py-20 lg:py-24">
            {/* Left */}
            <FadeIn>
              <Eyebrow>Capital readiness for emerging-market founders</Eyebrow>
              <h1 style={{ fontSize: 'clamp(42px,5.6vw,72px)', lineHeight: 1.05, letterSpacing: '-0.03em', fontWeight: 700, marginTop: 20 }}>
                Get investor-ready. Then{' '}
                <span style={{ position: 'relative', display: 'inline' }}>
                  raise with confidence.
                  <span aria-hidden="true" style={{ position: 'absolute', left: -2, right: -2, bottom: 6, height: '0.32em', background: BRAND_SOFT, zIndex: -1, transform: 'skewX(-4deg)', display: 'block' }} />
                </span>
              </h1>
              <p style={{ fontSize: 20, color: INK2, marginTop: 26, marginBottom: 32, maxWidth: 600, lineHeight: 1.6 }}>
                One live score that turns "we think we're fundable" into proof. We assess your readiness, close the gaps with you, and help you raise — across MENA, the UK and the US.
              </p>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <Link to={ctaPath} style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, background: INK, color: CREAM, border: `2px solid ${INK}`, textDecoration: 'none' }}>
                  Get your free readiness score →
                </Link>
                <a href="#what" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, background: 'transparent', color: INK, border: `2px solid ${INK}`, textDecoration: 'none' }}>
                  See how it works
                </a>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginTop: 30, fontSize: 14, color: MUTED }}>
                <Stars />
                <span><b style={{ color: INK }}>Built by the team behind Wealthface</b> · 300+ startups advised</span>
              </div>
            </FadeIn>

            {/* Right: dashboard mockup */}
            <FadeIn delay={160}>
              <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 26, boxShadow: '0 26px 60px -24px rgba(23,38,58,.30)', padding: 22 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                  <b style={{ fontFamily: 'Montserrat', fontSize: 17 }}>Your readiness score</b>
                  <span style={{ fontSize: 11.5, fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: BRAND_SOFT, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Zap className="w-3.5 h-3.5" style={{ color: BRAND }} /> Live</span>
                </div>
                {/* Donut row */}
                <div style={{ display: 'flex', gap: 18, alignItems: 'center', background: PAPER, border: `1px solid ${LINE}`, borderRadius: 16, padding: 16, marginBottom: 14 }}>
                  <div style={{ position: 'relative', width: 90, height: 90, flexShrink: 0 }}>
                    <svg width="90" height="90" viewBox="0 0 90 90" style={{ transform: 'rotate(-90deg)' }}>
                      <circle cx="45" cy="45" r="38" fill="none" stroke="#D7E1F5" strokeWidth="11" />
                      <circle cx="45" cy="45" r="38" fill="none" stroke={BRAND} strokeWidth="11" strokeLinecap="round" strokeDasharray="239" strokeDashoffset="76" />
                    </svg>
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Montserrat', fontSize: 23, fontWeight: 800 }}>68%</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <b style={{ fontFamily: 'Montserrat', fontSize: 15, display: 'block' }}>12 points from investor-ready</b>
                    <small style={{ color: MUTED, fontSize: 12.5 }}>Target: 80% — the investor-ready line</small>
                    <div style={{ height: 8, background: '#D7E1F5', borderRadius: 6, marginTop: 9, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: '68%', background: `linear-gradient(90deg,${BRAND},${BRAND_DEEP})` }} />
                    </div>
                  </div>
                </div>
                {/* Checklist items */}
                {[
                  { icon: 'check', title: 'Financial model', sub: 'Reviewed & investor-grade', st: 'Done', done: true },
                  { icon: 'check', title: 'Pitch deck', sub: 'Narrative & metrics aligned', st: 'Done', done: true },
                  { icon: 'clock', title: 'Data room', sub: '2 documents outstanding', st: 'In progress', done: false },
                  { icon: 'chart', title: 'Investor match preview', sub: '8 investors fit your stage', st: 'Preview', done: false },
                ].map((item, i, arr) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', border: `1px solid ${LINE}`, borderRadius: 13, marginBottom: i < arr.length - 1 ? 9 : 0, background: CREAM }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: item.done ? '#D7F0E3' : BRAND }}>
                      {item.icon === 'check' && <svg width="15" height="15" viewBox="0 0 24 24"><path d="M5 12l5 5L20 7" stroke="#15824E" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                      {item.icon === 'clock' && <svg width="15" height="15" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5" stroke="#fff" strokeWidth="2.2" fill="none" /><path d="M12 7v5l3 2" stroke="#fff" strokeWidth="2.3" strokeLinecap="round" fill="none" /></svg>}
                      {item.icon === 'chart' && <svg width="15" height="15" viewBox="0 0 24 24"><path d="M4 15l5-5 4 4 7-7" stroke="#fff" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <b style={{ fontSize: 13.5, fontWeight: 600, display: 'block' }}>{item.title}</b>
                      <small style={{ color: MUTED, fontSize: 11.5 }}>{item.sub}</small>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, color: item.done ? GREEN : CREAM, background: item.done ? '#D7F0E3' : INK }}>
                      {item.st}
                    </span>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </header>

      {/* ── Problem ── */}
      <section style={{ padding: '104px 0' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn className="text-center max-w-[720px] mx-auto">
            <Eyebrow>The gap</Eyebrow>
            <h2 style={{ fontSize: 'clamp(31px,4.2vw,50px)', margin: '18px 0 14px', lineHeight: 1.1, letterSpacing: '-0.022em' }}>
              Most founders aren't rejected for being un-fundable. They're rejected for not being{' '}
              <em style={{ fontStyle: 'normal', background: `linear-gradient(180deg,transparent 62%,${BRAND_SOFT} 62%)` }}>ready</em>.
            </h2>
            <p style={{ fontSize: 19, color: INK2, maxWidth: 640, margin: '0 auto' }}>
              Weak financials, a vague raise, a half-built data room — these kill deals quietly. Conncct makes readiness measurable, then helps you fix it before you walk into the room.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── How it works ── */}
      <section id="how" style={{ background: PAPER, padding: '104px 0' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn className="text-center max-w-[720px] mx-auto">
            <Eyebrow>How it works</Eyebrow>
            <h2 style={{ fontSize: 'clamp(31px,4.2vw,50px)', margin: '18px 0 14px', lineHeight: 1.1 }}>Score it. Fix it. Raise it.</h2>
            <p style={{ fontSize: 19, color: INK2 }}>A clear path from "where do we stand?" to capital in the bank.</p>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-11">
            {[
              { h: 'Get your score', p: "A free readiness score shows exactly where you stand and what's holding the raise back." },
              { h: 'Assess with an advisor', p: 'A Capital Assessment and a 40-minute advisor session turn the score into a concrete plan.' },
              { h: 'Close the gaps', p: 'We work with you on the model, pitch, data room and KPIs — your live score climbs toward 80%.' },
              { h: 'Raise', p: "At investor-ready, we help you prepare and approach the right investors. Full raise support is rolling out." },
            ].map((step, i) => (
              <FadeIn key={i} delay={i * 80}>
                <div style={{ paddingTop: 18, borderTop: `3px solid ${BRAND}` }}>
                  <span style={{ fontFamily: 'Montserrat', fontSize: 34, color: BRAND_DEEP, fontWeight: 700, display: 'block', marginBottom: 6 }}>0{i + 1}</span>
                  <h3 style={{ fontSize: 18, marginBottom: 6, fontWeight: 700 }}>{step.h}</h3>
                  <p style={{ color: MUTED, fontSize: 14.5 }}>{step.p}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── What we do (tabs) ── */}
      <section id="what" style={{ padding: '104px 0' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn className="text-center max-w-[720px] mx-auto">
            <Eyebrow>What we do</Eyebrow>
            <h2 style={{ fontSize: 'clamp(31px,4.2vw,50px)', margin: '18px 0 14px', lineHeight: 1.1 }}>From first score to signed term sheet.</h2>
          </FadeIn>
          {/* Tab buttons */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', margin: '38px 0 30px' }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: '11px 20px', borderRadius: 999, border: `1.5px solid ${activeTab === t.id ? INK : LINE}`, background: activeTab === t.id ? INK : '#fff', color: activeTab === t.id ? CREAM : INK, fontWeight: 600, fontSize: 14.5, cursor: 'pointer', fontFamily: 'Montserrat', transition: '.2s' }}>
                {t.label}
              </button>
            ))}
          </div>
          {/* Panel */}
          <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 24, padding: 40, boxShadow: '0 8px 24px -14px rgba(23,38,58,.22)' }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h3 style={{ fontSize: 27, marginBottom: 10, fontWeight: 700 }}>{tab.h}</h3>
                <p style={{ color: INK2, fontSize: 16 }}>{tab.p}</p>
                <ul style={{ listStyle: 'none', marginTop: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {tab.features.map((f, i) => (
                    <li key={i} style={{ display: 'flex', gap: 10, fontSize: 15, alignItems: 'flex-start' }}>
                      <CheckIcon />
                      {f}
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: 20, fontWeight: 700 }}>
                  {tab.price} <span style={{ color: MUTED, fontWeight: 500 }}>{tab.priceSub}</span>
                </div>
              </div>
              <div className="order-first lg:order-last" style={{ background: PAPER, border: `1px solid ${LINE}`, borderRadius: 18, padding: 22 }}>
                {tab.rows.map(([k, v], i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < tab.rows.length - 1 ? `1px solid ${LINE}` : 'none', fontSize: 13.5 }}>
                    <span>{k}</span>
                    <b>{v}</b>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Feature: The Score ── */}
      <section style={{ background: INK, color: CREAM, padding: '104px 0' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <FadeIn>
              <Eyebrow dark>The keystone</Eyebrow>
              <h2 style={{ fontSize: 'clamp(30px,3.8vw,46px)', color: CREAM, margin: '18px 0 14px', lineHeight: 1.1 }}>One score. From idea to investor-ready.</h2>
              <p style={{ color: '#AEB9D6', fontSize: 17, marginTop: 14 }}>
                It starts free and static — a snapshot of where you stand. Subscribe and it comes alive: it moves every month as you close gaps, nudges you toward the next milestone, and alerts you the moment you cross 80% — the line where investors take you seriously.
              </p>
              <div style={{ marginTop: 24 }}>
                <Link to={ctaPath} style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, background: BRAND, color: '#fff', border: `2px solid ${BRAND}`, textDecoration: 'none' }}>
                  Get your free score →
                </Link>
              </div>
            </FadeIn>
            <FadeIn delay={80}>
              <div style={{ background: '#101A38', border: '1px solid #233056', borderRadius: 22, padding: 30, textAlign: 'center' }}>
                <div style={{ fontFamily: 'Montserrat', fontSize: 84, color: BRAND_BRIGHT, lineHeight: 1, fontWeight: 800 }}>80<span style={{ fontSize: 38 }}>%</span></div>
                <div style={{ height: 12, background: '#243154', borderRadius: 8, margin: '18px 0 8px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: '80%', background: BRAND }} />
                </div>
                <small style={{ color: '#8C99BC', fontSize: 13 }}>The investor-ready line — where the raise begins.</small>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── Markets ── */}
      <section id="markets" style={{ padding: '104px 0' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn className="text-center max-w-[720px] mx-auto">
            <Eyebrow>Three markets, day one</Eyebrow>
            <h2 style={{ fontSize: 'clamp(31px,4.2vw,50px)', margin: '18px 0 14px', lineHeight: 1.1 }}>Built where the capital is.</h2>
            <p style={{ fontSize: 19, color: INK2, maxWidth: 640, margin: '0 auto' }}>Emerging-market founders, global investors. We bridge both.</p>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-11">
            {MARKETS.map((m, i) => (
              <FadeIn key={i} delay={i * 80}>
                <div className="group" style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 20, padding: 26, transition: 'transform .2s, box-shadow .2s', cursor: 'default' }}
                  onMouseEnter={e => { const el = e.currentTarget as HTMLDivElement; el.style.transform = 'translateY(-5px)'; el.style.boxShadow = '0 26px 60px -24px rgba(23,38,58,.30)'; }}
                  onMouseLeave={e => { const el = e.currentTarget as HTMLDivElement; el.style.transform = ''; el.style.boxShadow = ''; }}>
                  <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: BRAND_DEEP }}>{m.flag}</span>
                  <h3 style={{ fontSize: 22, margin: '8px 0 6px', fontWeight: 700 }}>{m.h}</h3>
                  <p style={{ color: MUTED, fontSize: 14.5 }}>{m.p}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" style={{ background: PAPER, padding: '104px 0' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn className="text-center max-w-[720px] mx-auto">
            <Eyebrow>Pricing</Eyebrow>
            <h2 style={{ fontSize: 'clamp(31px,4.2vw,50px)', margin: '18px 0 14px', lineHeight: 1.1 }}>Start free. Pay as you get serious.</h2>
            <p style={{ fontSize: 19, color: INK2 }}>Begin with a free score, go deeper when you're ready to raise.</p>
          </FadeIn>
          {/* Ladder */}
          <FadeIn>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', marginTop: 36 }}>
              {LADDER.map((l, i) => (
                <div key={i} style={{ background: '#fff', border: `1.5px solid ${LINE}`, borderRadius: 16, padding: '16px 20px', textAlign: 'center', minWidth: 150 }}>
                  <b style={{ fontFamily: 'Montserrat', fontSize: 18, display: 'block' }}>{l.name}</b>
                  <small style={{ color: MUTED, fontSize: 12.5 }}>{l.sub}</small>
                  <div style={{ fontWeight: 700, fontSize: 14, marginTop: 6 }}>{l.price}</div>
                </div>
              ))}
            </div>
          </FadeIn>
          {/* Tiers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
            {TIERS.map((tier, i) => (
              <FadeIn key={i} delay={i * 80} className="h-full">
                <div style={{ background: '#fff', border: `1.5px solid ${tier.pop ? INK : LINE}`, borderRadius: 22, padding: 30, display: 'flex', flexDirection: 'column', boxShadow: tier.pop ? '0 26px 60px -24px rgba(23,38,58,.30)' : undefined, position: 'relative', height: '100%', transition: 'transform .25s, box-shadow .25s' }}
                  onMouseEnter={e => { const el = e.currentTarget as HTMLDivElement; el.style.transform = 'translateY(-6px)'; el.style.boxShadow = '0 26px 60px -24px rgba(23,38,58,.30)'; }}
                  onMouseLeave={e => { const el = e.currentTarget as HTMLDivElement; el.style.transform = ''; el.style.boxShadow = tier.pop ? '0 26px 60px -24px rgba(23,38,58,.30)' : ''; }}>
                  {tier.pop && (
                    <span style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: BRAND, color: '#fff', border: `2px solid ${BRAND}`, fontSize: 12, fontWeight: 700, padding: '4px 14px', borderRadius: 999, whiteSpace: 'nowrap' }}>Most popular</span>
                  )}
                  <span style={{ fontSize: 13, fontWeight: 700, color: MUTED }}>{tier.tag}</span>
                  <h3 style={{ fontSize: 23, margin: '6px 0 4px', fontWeight: 700 }}>{tier.name}</h3>
                  <div style={{ fontFamily: 'Montserrat', fontSize: 40, margin: '14px 0 0', fontWeight: 800 }}>{tier.price}</div>
                  <ul style={{ listStyle: 'none', margin: '18px 0 24px', display: 'flex', flexDirection: 'column', gap: 11, flex: 1 }}>
                    {tier.features.map((f, j) => (
                      <li key={j} style={{ display: 'flex', gap: 10, fontSize: 14.5, alignItems: 'flex-start' }}>
                        <CheckIcon />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link to={ctaPath} style={{ display: 'block', textAlign: 'center', padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, background: tier.pop ? INK : 'transparent', color: tier.pop ? CREAM : INK, border: `2px solid ${INK}`, textDecoration: 'none', marginTop: 'auto' }}>
                    {tier.cta}
                  </Link>
                </div>
              </FadeIn>
            ))}
          </div>
          <p style={{ fontSize: 13, color: MUTED, marginTop: 16, textAlign: 'center' }}>Your $99 Capital Assessment is credited against any package. Prices in USD.</p>
          {/* Raise bar */}
          <FadeIn>
            <div style={{ background: INK, color: CREAM, borderRadius: 22, padding: '34px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, marginTop: 34, flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ color: CREAM, fontSize: 23, fontWeight: 700 }}>Planning a raise?</h3>
                <p style={{ color: '#AEB9D6', fontSize: 14.5, marginTop: 4, maxWidth: 520 }}>
                  Our advisors get you raise-ready — model, pitch, data room and investor prep. Full raise execution (including success-fee engagements) is rolling out, so talk to us about timing.
                </p>
              </div>
              <Link to={ctaPath} style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, background: BRAND, color: '#fff', border: `2px solid ${BRAND}`, textDecoration: 'none', flexShrink: 0 }}>
                Talk about your raise →
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── Stats band ── */}
      <section style={{ background: BRAND, padding: '72px 0' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            {[
              { to: 300, suffix: '+', label: 'Startups advised by our team' },
              { to: 3, suffix: '', label: 'Markets — MENA, London, US' },
              { to: 80, suffix: '%', label: 'The investor-ready score line' },
              { to: 1, suffix: '', label: 'Exit behind the team (Wealthface)' },
            ].map((s, i) => (
              <FadeIn key={i} delay={i * 80}>
                <b style={{ fontFamily: 'Montserrat', fontSize: 'clamp(38px,5vw,56px)', display: 'block', color: '#fff', fontWeight: 800 }}>
                  <CounterStat to={s.to} suffix={s.suffix} />
                </b>
                <span style={{ fontSize: 14, color: 'rgba(255,255,255,.85)' }}>{s.label}</span>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── Reviews ── */}
      <section style={{ padding: '104px 0' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn className="text-center max-w-[720px] mx-auto">
            <Eyebrow>Founders we've backed</Eyebrow>
            <h2 style={{ fontSize: 'clamp(31px,4.2vw,50px)', margin: '18px 0 14px', lineHeight: 1.1 }}>From "not sure" to funded.</h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-11">
            {REVIEWS.map((r, i) => (
              <FadeIn key={i} delay={i * 80}>
                <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 20, padding: 24 }}>
                  <Stars />
                  <p style={{ fontSize: 15, margin: '12px 0 16px' }}>"{r.q}"</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: BRAND, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontFamily: 'Montserrat', color: '#fff', fontSize: 15 }}>{r.av}</div>
                    <div>
                      <b style={{ fontSize: 14 }}>{r.name}</b>
                      <small style={{ display: 'block', color: MUTED, fontSize: 12.5 }}>{r.role}</small>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" style={{ background: PAPER, padding: '104px 0' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn className="text-center">
            <Eyebrow>FAQ</Eyebrow>
            <h2 style={{ fontSize: 'clamp(30px,4vw,44px)', margin: '18px 0 0', lineHeight: 1.1 }}>Questions, answered.</h2>
          </FadeIn>
          <div style={{ maxWidth: 800, margin: '40px auto 0' }}>
            {FAQS.map((faq, i) => (
              <div key={i} style={{ borderBottom: `1px solid ${LINE}` }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left', padding: '22px 4px', fontSize: 18, fontFamily: 'Montserrat', fontWeight: 600, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, color: INK }}>
                  {faq.q}
                  <span style={{ fontSize: 25, transition: 'transform .3s', transform: openFaq === i ? 'rotate(45deg)' : 'none', color: BRAND_DEEP, flexShrink: 0 }}>+</span>
                </button>
                <div style={{ maxHeight: openFaq === i ? 280 : 0, overflow: 'hidden', transition: 'max-height .32s ease', color: INK2, fontSize: 15.5 }}>
                  <p style={{ padding: '0 4px 22px' }}>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section style={{ padding: '104px 0' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn>
            <div style={{ background: BRAND, borderRadius: 30, padding: '70px 40px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
              <div aria-hidden="true" style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(#17263A .9px,transparent .9px)', backgroundSize: '24px 24px', opacity: 0.07 }} />
              <h2 style={{ fontSize: 'clamp(34px,4.6vw,56px)', position: 'relative', color: '#fff', lineHeight: 1.05, fontWeight: 700 }}>See where you stand — free.</h2>
              <p style={{ fontSize: 18, color: 'rgba(255,255,255,.92)', maxWidth: 560, margin: '16px auto 28px', position: 'relative' }}>
                Get your readiness score in minutes, then book a Capital Assessment when you're ready to close the gap and raise.
              </p>
              <Link to={ctaPath} style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, background: INK, color: CREAM, border: `2px solid ${INK}`, textDecoration: 'none', position: 'relative' }}>
                Get your free readiness score →
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
